import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import UserTable from '../components/UserTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getUsers, makeUserAdmin, removeUserAdmin, updateUserField } from '../lib/admin-api';
import { Loader2, Search, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-sonner-toast';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (reset = true) => {
    try {
      setLoading(true);
      const result = await getUsers(reset ? null : lastVisible);

      if (reset) {
        setUsers(result.users);
      } else {
        setUsers([...users, ...result.users]);
      }

      setLastVisible(result.lastVisible);
      setHasMore(result.users.length === 20); // Assuming pageSize is 20
    } catch (error) {
      
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    // In a real implementation, you would call an API with the search query
    
    // For now, just reset and fetch all users
    fetchUsers();
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      fetchUsers(false);
    }
  };

  const handleRefresh = () => {
    fetchUsers();
  };

  const handleViewUser = (userId: string) => {
    
    // Navigate to user details page or open modal
  };

  const handleEditUser = (userId: string) => {
    
    // Open edit user modal
  };

  const handleMakeAdmin = async (userId: string) => {
    try {
      setLoading(true);
      

      // Find the user in the current list
      const user = users.find((u) => u.uid === userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Call API to make user an admin
      const success = await makeUserAdmin(userId, user.email || '', user.displayName || '');

      if (success) {
        // Update the user in the local state
        setUsers((prevUsers) =>
          prevUsers.map((u) => (u.uid === userId ? { ...u, isAdmin: true } : u))
        );

        toast({
          title: 'Success',
          description: 'User has been made an admin',
        });
      } else {
        throw new Error('Failed to make user admin');
      }
    } catch (error) {
      
      toast({
        title: 'Error',
        description: 'Failed to make user an admin',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAdmin = async (userId: string) => {
    try {
      setLoading(true);
      

      // Call API to remove admin privileges
      const success = await removeUserAdmin(userId);

      if (success) {
        // Update the user in the local state
        setUsers((prevUsers) =>
          prevUsers.map((u) => (u.uid === userId ? { ...u, isAdmin: false } : u))
        );

        toast({
          title: 'Success',
          description: 'Admin privileges have been removed',
        });
      } else {
        throw new Error('Failed to remove admin privileges');
      }
    } catch (error) {
      
      toast({
        title: 'Error',
        description: 'Failed to remove admin privileges',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle updating a user field
  const handleUpdateField = async (userId: string, field: string, value: any): Promise<boolean> => {
    try {
      

      // Call API to update the field
      const success = await updateUserField(userId, field, value);

      if (success) {
        // Update the user in the local state
        setUsers((prevUsers) =>
          prevUsers.map((u) => {
            if (u.uid !== userId) return u;

            // Create a copy of the user
            const updatedUser = { ...u };

            // Handle nested fields like gameState.skipCount
            if (field.includes('.')) {
              const [parent, child] = field.split('.');
              if (!updatedUser[parent]) updatedUser[parent] = {};
              updatedUser[parent] = { ...updatedUser[parent], [child]: value };
            } else {
              // Handle direct fields
              updatedUser[field] = value;
            }

            return updatedUser;
          })
        );

        toast({
          title: 'Success',
          description: `User field "${field}" has been updated`,
        });

        return true;
      } else {
        throw new Error(`Failed to update user field ${field}`);
      }
    } catch (error) {
      
      toast({
        title: 'Error',
        description: `Failed to update user field "${field}"`,
        variant: 'destructive',
      });
      return false;
    }
  };

  // Handle updating premium status
  const handleUpdatePremiumStatus = async (
    userId: string,
    isPremium: boolean
  ): Promise<boolean> => {
    try {
      

      // Call API to update the isPremium field
      const success = await updateUserField(userId, 'isPremium', isPremium);

      if (success) {
        // Update the user in the local state
        setUsers((prevUsers) => prevUsers.map((u) => (u.uid === userId ? { ...u, isPremium } : u)));

        toast({
          title: 'Success',
          description: `User premium status has been ${isPremium ? 'enabled' : 'disabled'}`,
        });

        return true;
      } else {
        throw new Error('Failed to update premium status');
      }
    } catch (error) {
      
      toast({
        title: 'Error',
        description: 'Failed to update premium status',
        variant: 'destructive',
      });
      return false;
    }
  };

  const filteredUsers = searchQuery
    ? users.filter(
        (user) =>
          user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.displayName?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  return (
    <AdminLayout title="User Information">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button variant="outline" size="icon" onClick={handleSearch}>
              <Search className="h-4 w-4" />
              <span className="sr-only">Search</span>
            </Button>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {loading && users.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <UserTable
              users={filteredUsers}
              onViewUser={handleViewUser}
              onEditUser={handleEditUser}
              onMakeAdmin={handleMakeAdmin}
              onRemoveAdmin={handleRemoveAdmin}
              onUpdateField={handleUpdateField}
              onUpdatePremiumStatus={handleUpdatePremiumStatus}
            />

            {hasMore && (
              <div className="flex justify-center mt-4">
                <Button variant="outline" onClick={handleLoadMore} disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default UserManagement;

